import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import Swal from 'sweetalert2'
import { lotteryService, type BetSlip } from '../../services/lotteryService'
import type { LotteryRound } from '../../services/lotteryService'
import { numberSetService, type CustomerNumberSet } from '../../services/numberSetService'
import type { BettingPackage } from './ModernLobbyHuayLotteryTermsPage'
import { getCustomPayoutRateForRoundLimit } from '../../utils/roundLimitNumberUtils'

/** Toast helper (user-modern ไม่มี react-hot-toast ใช้ Swal แทน) */
function toast(msg: string, opts?: { icon?: string; position?: string; duration?: number }) {
  Swal.fire({
    toast: true,
    position: (opts?.position as any) || 'top',
    icon: opts?.icon === '⚠️' ? 'warning' : undefined,
    title: msg,
    showConfirmButton: false,
    timer: opts?.duration ?? 3000,
    timerProgressBar: true,
  })
}
function toastSuccess(msg: string, opts?: { position?: string; duration?: number }) {
  Swal.fire({
    toast: true,
    position: (opts?.position as any) || 'top',
    icon: 'success',
    title: msg,
    showConfirmButton: false,
    timer: opts?.duration ?? 3000,
    timerProgressBar: true,
  })
}

/** Map prizeTypeCode from API to internal prizeCode (same as user-classic LotteryBetSideLeft) */
const PRIZE_TYPE_CODE_TO_PRIZE_CODE: Record<string, string> = {
  '3_DIGIT_TOP': 'top3',
  '3_DIGIT_BOTTOM': 'down3',
  '3_DIGIT_TOD': 'tod3',
  '3_DIGIT_REVERSE': 'tod3reverse',
  '2_DIGIT_TOP': 'top2',
  '2_DIGIT_BOTTOM': 'down2',
  '2_DIGIT_TOD': 'tod2',
  '2_DIGIT_REVERSE': 'tod2reverse',
  'RUNNING_TOP': 'top1',
  'RUNNING_BOTTOM': 'down1',
  '4_DIGIT_TOP': 'top4',
  '4_DIGIT_TOD': 'tod4',
  '5_DIGIT_TOD': 'tod5',
  'FIXED_POSITION_ONES': 'puk1',
  'FIXED_POSITION_TENS': 'puk10',
  'FIXED_POSITION_HUNDREDS': 'puk100',
}

/** ชื่อแสดงตาม prizeCode (เดียวกับ user-classic) */
export const PRIZE_CODE_NAME_MAP: Record<string, string> = {
  top3: '3 ตัวบน',
  down3: '3 ตัวล่าง',
  tod3: '3 ตัวโต๊ด',
  tod3reverse: '3 ตัวกลับ',
  top2: '2 ตัวบน',
  down2: '2 ตัวล่าง',
  tod2: '2 ตัวโต๊ด',
  tod2reverse: '2 ตัวกลับ',
  top1: 'วิ่งบน',
  down1: 'วิ่งล่าง',
  top3same: 'ตอง',
  top3pick1group: 'เลขปัก',
  top5fourgroup: '4-5 ตัว',
}

/** Legacy id จาก BET_TYPES_MAIN → prizeCode (เมื่อมี package ใช้ prizeCode) */
const LEGACY_ID_TO_PRIZE_CODE: Record<string, string> = {
  '3top': 'top3',
  '3tod': 'tod3',
  '3rev': 'tod3reverse',
  '2top': 'top2',
  '2low': 'down2',
  '2rev': 'tod2reverse',
  '2tod': 'tod2',
  '3low': 'down3',
  tong: 'top3same',
  'run-top': 'top1',
  'run-low': 'down1',
  puk: 'top3pick1group',
  '45': 'top5fourgroup',
}

/** ประเภทแทง ตรง huaybet.html — ใช้เมื่อไม่มี selectedPackage (fallback) */
export const BET_TYPES_MAIN = [
  { id: '3top', label: '3 ตัวบน', price: '1,000', multiplier: 1000, bgClass: 'bg_gd_quinary', disabled: false },
  { id: '3tod', label: '3 ตัวโต๊ด', price: '150', multiplier: 150, bgClass: 'bg_gd_quinary', disabled: false },
  { id: '3rev', label: '+3 กลับ', price: '⇄', multiplier: 1000, bgClass: 'bg_gd_quinary', disabled: false },
  { id: '2top', label: '2 ตัวบน', price: '100', multiplier: 100, bgClass: 'bg_gd_danger', disabled: false },
  { id: '2low', label: '2 ตัวล่าง', price: '100', multiplier: 100, bgClass: 'bg_gd_danger', disabled: false },
  { id: '2rev', label: '+2 กลับ', price: '⇄', multiplier: 100, bgClass: 'bg_gd_danger', disabled: false },
  { id: '2tod', label: '2 ตัวโต๊ด', price: '0', multiplier: 0, bgClass: 'bg_gd_danger', disabled: true },
  { id: '3low', label: '3 ตัวล่าง', price: '0', multiplier: 0, bgClass: 'bg_gd_quinary', disabled: true },
  { id: 'tong', label: '+ ตอง', price: '1,000', multiplier: 1000, bgClass: 'bg_gd_warning', disabled: false },
  { id: 'run-top', label: 'วิ่งบน', price: '3.2', multiplier: 3.2, bgClass: 'bg_gd_success', disabled: false },
  { id: 'run-low', label: 'วิ่งล่าง', price: '4.2', multiplier: 4.2, bgClass: 'bg_gd_success', disabled: false },
  { id: 'puk', label: 'เลขปัก', price: '', multiplier: 0, bgClass: 'bg_gd_info', disabled: true },
  { id: '45', label: '4-5 ตัว', price: '', multiplier: 0, bgClass: 'bg_gd_info', disabled: true },
]

/** สำหรับ addBetItem ใช้เฉพาะประเภทที่ disabled: false และ multiplier > 0 */
export const BET_TYPES = BET_TYPES_MAIN.filter((t) => !t.disabled && t.multiplier > 0)

export interface HuayBetItem {
  id: string
  number: string
  betType: string
  betTypeLabel: string
  amount: number
  multiplier: number
  discount: number
  payout: number
}

export interface ModernLobbyHuayBetSideLeftProps {
  title: string
  logo: string
  roundLabel: string
  countdown: string
  /** รอบจาก API (จาก listall → lottery-terms → bet) ใช้สำหรับ countdown จริงและอัตราจ่ายปรับราคา */
  round?: LotteryRound
  /** แพ็คเกจที่เลือกจาก lottery-terms (เดียวกับ user-classic) ถ้ามีจะใช้ rewardMap / getPayoutRate แทนค่าตายตัว */
  selectedPackage?: BettingPackage
  selectedBetType: string
  setSelectedBetType: (id: string) => void
  numbers: string[]
  handleNumberInput: (index: number, value: string) => void
  appendDigit: (d: number) => void
  backspace: () => void
  addBetItem?: (data: { number: string; betType: string; betTypeLabel: string; amount: number; multiplier: number; discount: number }) => void
  betItems: HuayBetItem[]
  defaultAmount: number
  /** ล้างช่องตัวเลขหลังเพิ่มโพย (จาก user-classic) */
  setNumbers?: (v: string[] | ((prev: string[]) => string[])) => void
  /** Ref ที่ parent เรียกเมื่อส่งโพยสำเร็จ — ใช้ล้างประเภทการแทงและรายการที่เลือก */
  clearSelectionRef?: React.MutableRefObject<(() => void) | null>
}

/* ตรง huaybet.html: row-gap: 4px; margin: 0 -2px !important และ padding: 0 2px !important */
const ROW_STYLE = { rowGap: '4px', margin: '0 -2px' }
const COL_PAD = { padding: '0 2px' }

/** ฝั่งซ้ายหน้าแทงหวย — logic เดียวกับ user-classic LotteryBetSideLeft, UI แบบ modern */
const group3 = ['top3', 'down3', 'tod3', 'tod3reverse']
const group2 = ['top2', 'down2', 'tod2', 'tod2reverse']
const groupRun = ['top1', 'down1']
const groupPick = ['top3pick1group']
const group45 = ['top5fourgroup']

const getGroup = (code: string): string => {
  if (['top3same', ...group3].includes(code)) return 'group3'
  if (group2.includes(code)) return 'group2'
  if (groupRun.includes(code)) return 'groupRun'
  if (groupPick.includes(code)) return 'groupPick'
  if (group45.includes(code)) return 'group45'
  return 'other'
}

const ModernLobbyHuayBetSideLeft: React.FC<ModernLobbyHuayBetSideLeftProps> = ({
  title,
  logo,
  roundLabel,
  countdown: countdownProp,
  round,
  selectedPackage,
  selectedBetType,
  setSelectedBetType,
  numbers,
  handleNumberInput,
  appendDigit,
  backspace,
  addBetItem,
  betItems,
  defaultAmount,
  setNumbers,
  clearSelectionRef,
}) => {
  const [panel3Active, setPanel3Active] = useState(0)
  const [countdown, setCountdown] = useState(countdownProp)
  const [selectedPrizeCodes, setSelectedPrizeCodes] = useState<string[]>(() => {
    const code = LEGACY_ID_TO_PRIZE_CODE[selectedBetType] ?? selectedBetType
    return code ? [code] : []
  })
  const [selectedPickNumbers, setSelectedPickNumbers] = useState<string[]>([])
  const [special2DigitType, setSpecial2DigitType] = useState<'front' | 'back' | '19door' | null>(null)
  const [showNumberPanel, setShowNumberPanel] = useState(false)
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([])
  const [showNumberSetModal, setShowNumberSetModal] = useState(false)
  const [numberSetList, setNumberSetList] = useState<CustomerNumberSet[]>([])
  const [loadingNumberSets, setLoadingNumberSets] = useState(false)
  const [showOldSlipModal, setShowOldSlipModal] = useState(false)
  const [oldSlipList, setOldSlipList] = useState<BetSlip[]>([])
  const [loadingOldSlips, setLoadingOldSlips] = useState(false)
  const [showOnlyCurrentPackage, setShowOnlyCurrentPackage] = useState(false)

  const usePackage = Boolean(selectedPackage?.prizeConfigs?.length)
  const inputCount = useMemo(() => {
    if (!selectedPrizeCodes.length) return 5
    const first = selectedPrizeCodes[0]
    if (['top1', 'down1'].includes(first)) return 1
    if (first === 'top3pick1group') return 1
    if (first === 'top5fourgroup') {
      const pick = selectedPickNumbers[0] || ''
      return pick === '5 ตัวโต๊ด' ? 5 : 4
    }
    if (['top2', 'down2', 'tod2', 'tod2reverse'].includes(first)) return 2
    return 3
  }, [selectedPrizeCodes, selectedPickNumbers])

  const currentNumber = useMemo(() => numbers.slice(0, inputCount).join('').trim(), [numbers, inputCount])

  const selectedNumbersFromBetItems = useMemo(() => {
    if (!betItems?.length) return new Set<string>()
    const set = new Set<string>()
    betItems.forEach((item) => {
      if (item.number) set.add(item.number)
    })
    return set
  }, [betItems])

  // Sync selectedPrizeCodes -> parent
  useEffect(() => {
    const first = selectedPrizeCodes[0]
    if (first) setSelectedBetType(first)
  }, [selectedPrizeCodes, setSelectedBetType])

  // หลังส่งโพยสำเร็จ parent เรียก clearSelectionRef.current() เพื่อล้างปุ่มประเภทการแทงและรายการที่เลือก
  useEffect(() => {
    if (!clearSelectionRef) return
    clearSelectionRef.current = () => {
      setSelectedPrizeCodes([])
      setSelectedPickNumbers([])
      setSpecial2DigitType(null)
      setNumbers?.(Array(5).fill(''))
    }
    return () => {
      clearSelectionRef.current = null
    }
  }, [clearSelectionRef, setNumbers])

  // Countdown จริงจาก round (เดียวกับ user-classic)
  useEffect(() => {
    if (!round?.betEndTime) {
      setCountdown(countdownProp)
      return
    }
    const update = () => setCountdown(lotteryService.formatTimeRemaining(round.betEndTime))
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [round?.betEndTime, countdownProp])

  // โหลดเลขชุดเมื่อเปิด modal
  useEffect(() => {
    if (!showNumberSetModal) return
    let cancelled = false
    setLoadingNumberSets(true)
    numberSetService
      .list()
      .then((list) => { if (!cancelled) setNumberSetList(list) })
      .catch(() => {
        if (!cancelled) {
          setNumberSetList([])
          toast('โหลดรายการเลขชุดไม่สำเร็จ', { icon: '⚠️' })
        }
      })
      .finally(() => { if (!cancelled) setLoadingNumberSets(false) })
    return () => { cancelled = true }
  }, [showNumberSetModal])

  // โหลดโพยเก่าเมื่อเปิด modal
  useEffect(() => {
    if (!showOldSlipModal) return
    let cancelled = false
    const run = async () => {
      try {
        const token = localStorage.getItem('accessToken')
        if (!token) { if (!cancelled) setOldSlipList([]); return }
        const payload = JSON.parse(atob(token.split('.')[1]))
        const userId = payload.sub || payload.id || payload.userId
        if (!userId) { if (!cancelled) setOldSlipList([]); return }
        setLoadingOldSlips(true)
        const opts: { limit: number; isSettled: boolean } = { limit: 50, isSettled: false }
        const { slips } = await lotteryService.getUserSlips(userId, opts)
        if (!cancelled) setOldSlipList((slips || []).slice(0, 50))
      } catch {
        if (!cancelled) setOldSlipList([])
      } finally {
        if (!cancelled) setLoadingOldSlips(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [showOldSlipModal])

  // rewardMap จาก selectedPackage (เดียวกับ user-classic)
  const rewardMap = useMemo(() => {
    const map: Record<string, number> = {}
    const cfg = selectedPackage?.prizeConfigs
    if (cfg?.length) {
      cfg.forEach((config) => {
        const isActive = (config as { isActive?: boolean }).isActive !== false
        if (isActive && config.payoutRate > 0) {
          const prizeCode = PRIZE_TYPE_CODE_TO_PRIZE_CODE[config.prizeTypeCode]
          if (prizeCode) {
            map[prizeCode] = config.payoutRate
            if (config.prizeTypeCode === '3_DIGIT_TOP') map['top3same'] = config.payoutRate
            if (['FIXED_POSITION_ONES', 'FIXED_POSITION_TENS', 'FIXED_POSITION_HUNDREDS'].includes(config.prizeTypeCode)) map['top3pick1group'] = config.payoutRate
            if (['4_DIGIT_TOP', '4_DIGIT_TOD', '5_DIGIT_TOD'].includes(config.prizeTypeCode)) map['top5fourgroup'] = config.payoutRate
            if (config.prizeTypeCode === '3_DIGIT_TOD') map['tod3reverse'] = config.payoutRate
            if (config.prizeTypeCode === '3_DIGIT_TOP' && map['tod3reverse'] === undefined) map['tod3reverse'] = config.payoutRate
            if (config.prizeTypeCode === '2_DIGIT_TOD') map['tod2reverse'] = config.payoutRate
            if (config.prizeTypeCode === '2_DIGIT_TOP' && map['tod2reverse'] === undefined) map['tod2reverse'] = config.payoutRate
          }
        }
      })
    }
    if (Object.keys(map).length === 0) {
      return {
        top3: 1000, down3: 1000, tod3: 150, tod3reverse: 150, top2: 100, down2: 100, tod2: 13, tod2reverse: 13,
        top1: 3.2, down1: 4.2, top3same: 1000, top3pick1group: 8, top5fourgroup: 0,
      }
    }
    return map
  }, [selectedPackage])

  type RoundLimit = { prizeTypeCode: string; numberSettings?: Array<{ number: string; customPayoutRate?: number; reverseNumber?: boolean }> }
  const roundLimits = (round as { roundLimits?: { prizeTypeLimits?: RoundLimit[] } } | undefined)?.roundLimits
  const getCustomPayoutRate = (prizeTypeCode: string, number: string): number | null =>
    getCustomPayoutRateForRoundLimit(roundLimits, prizeTypeCode, number)

  const getPayoutRate = (prizeCode: string, number?: string): number => {
    if (number) {
      const prizeTypeCode = Object.keys(PRIZE_TYPE_CODE_TO_PRIZE_CODE).find((k) => PRIZE_TYPE_CODE_TO_PRIZE_CODE[k] === prizeCode)
      if (prizeTypeCode) {
        const custom = getCustomPayoutRate(prizeTypeCode, number)
        if (custom !== null) return custom
      }
    }
    if (['puk1', 'puk10', 'puk100'].includes(prizeCode)) return rewardMap['top3pick1group'] ?? 0
    if (['top4', 'tod4', 'tod5'].includes(prizeCode) && (rewardMap['top5fourgroup'] ?? 0) > 0) {
      return rewardMap['top5fourgroup'] ?? 0
    }
    return rewardMap[prizeCode] ?? 0
  }

  const resolveBetPrizeCodeFromSlipItem = (prizeTypeCode: string): string | null => {
    const baseCode = PRIZE_TYPE_CODE_TO_PRIZE_CODE[prizeTypeCode]
    if (!baseCode) return null

    if (['puk1', 'puk10', 'puk100'].includes(baseCode)) {
      return (rewardMap['top3pick1group'] ?? 0) > 0 ? 'top3pick1group' : null
    }
    if (['top4', 'tod4', 'tod5'].includes(baseCode)) {
      if ((rewardMap['top5fourgroup'] ?? 0) > 0) return 'top5fourgroup'
      return (rewardMap[baseCode] ?? 0) > 0 ? baseCode : null
    }

    return (rewardMap[baseCode] ?? 0) > 0 ? baseCode : null
  }

  const getDiscount = (prizeCode: string): number => {
    if (!selectedPackage?.prizeConfigs) return 0

    if (prizeCode === 'top3pick1group') {
      const config = selectedPackage.prizeConfigs.find((c) =>
        ['FIXED_POSITION_ONES', 'FIXED_POSITION_TENS', 'FIXED_POSITION_HUNDREDS'].includes(c.prizeTypeCode)
      )
      return config?.discount ?? 0
    }
    if (prizeCode === 'top5fourgroup') {
      const config = selectedPackage.prizeConfigs.find((c) =>
        ['4_DIGIT_TOP', '4_DIGIT_TOD', '5_DIGIT_TOD'].includes(c.prizeTypeCode)
      )
      return config?.discount ?? 0
    }

    const prizeTypeCode = Object.keys(PRIZE_TYPE_CODE_TO_PRIZE_CODE).find((k) => PRIZE_TYPE_CODE_TO_PRIZE_CODE[k] === prizeCode)
    if (!prizeTypeCode) return 0
    const config = selectedPackage.prizeConfigs.find((c) => c.prizeTypeCode === prizeTypeCode)
    return config?.discount ?? 0
  }

  const expandReverseNumbers = (num: string, prizeCode: string): string[] => {
    if (prizeCode === 'tod3reverse' && num.length === 3) {
      const chars = num.split('')
      const set = new Set<string>()
      const permute = (arr: string[], start: number) => {
        if (start === arr.length - 1) { set.add(arr.join('')); return }
        for (let i = start; i < arr.length; i++) {
          const next = [...arr]
          ;[next[start], next[i]] = [next[i], next[start]]
          permute(next, start + 1)
        }
      }
      permute(chars, 0)
      return Array.from(set)
    }
    if (prizeCode === 'tod2reverse' && num.length === 2) return [num, num[1] + num[0]]
    return [num]
  }

  const handleApplyNumberSet = (set: CustomerNumberSet) => {
    if (!addBetItem) {
      toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', { icon: '⚠️' })
      return
    }
    let added = 0
    for (const item of set.items || []) {
      const prizeCode = PRIZE_TYPE_CODE_TO_PRIZE_CODE[item.prizeTypeCode]
      if (!prizeCode) {
        toast(`ประเภท "${item.typeLabel}" ยังไม่รองรับในรอบนี้`, { icon: '⚠️' })
        continue
      }
      const nums = item.numbers.split(/[,،\s]+/).map((s) => s.trim()).filter(Boolean)
      for (const num of nums) {
        const toAdd = expandReverseNumbers(num, prizeCode)
        for (const displayNum of toAdd) {
          const multiplier = getPayoutRate(prizeCode, displayNum)
          if (multiplier > 0) {
            addBetItem({
              number: displayNum,
              betType: prizeCode,
              betTypeLabel: item.typeLabel,
              amount: defaultAmount,
              multiplier,
              discount: getDiscount(prizeCode),
            })
            added += 1
          }
        }
      }
    }
    setShowNumberSetModal(false)
    if (added > 0) toastSuccess(`เพิ่ม ${added} รายการจากเลขชุด "${set.name}"`)
    else toast('ไม่มีรายการที่เพิ่มได้จากเลขชุดนี้', { icon: '⚠️' })
  }

  const handleApplyOldSlip = async (slip: BetSlip) => {
    if (!addBetItem || !slip.betItems?.length) {
      setShowOldSlipModal(false)
      return
    }
    const slipPackageName = slip.packageName || slip.packageCode || 'โพยเก่า'
    const currentPackageName = selectedPackage?.name || selectedPackage?.code || 'ปัจจุบัน'
    const isDifferentPackage = slip.packageCode && selectedPackage?.code && slip.packageCode !== selectedPackage.code
    if (isDifferentPackage) {
      const result = await Swal.fire({
        title: 'โพยคนละแพ็กเกจ',
        html: `โพยนี้สร้างจากแพ็กเกจ <strong>${slipPackageName}</strong><br><br>ยืนยันว่าจะดึงโพยเก่ามาใช้หรือไม่?<br><br><span class="text-muted">อัตราจ่ายและส่วนลดจะใช้ตามแพ็กเกจ <strong>${currentPackageName}</strong></span>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'ยืนยันดึงโพย',
        cancelButtonText: 'ยกเลิก',
        confirmButtonColor: '#198754',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
      })
      if (!result.isConfirmed) return
    }
    let added = 0
    let skipped = 0
    for (const item of slip.betItems) {
      const prizeCode = resolveBetPrizeCodeFromSlipItem(item.prizeTypeCode)
      if (!prizeCode) {
        skipped += 1
        continue
      }
      const multiplier = getPayoutRate(prizeCode, item.number)
      if (multiplier <= 0) {
        skipped += 1
        continue
      }
      addBetItem({
        number: item.number,
        betType: prizeCode,
        betTypeLabel: item.prizeTypeName || item.prizeTypeCode || prizeCode,
        amount: item.amount || defaultAmount,
        multiplier,
        discount: getDiscount(prizeCode),
      })
      added += 1
    }
    setShowOldSlipModal(false)
    if (added > 0) {
      const skipNote = skipped > 0 ? ` (ข้าม ${skipped} รายการที่แพ็กเกจนี้ไม่รองรับ)` : ''
      const msg = isDifferentPackage
        ? `ดึงโพย ${slip.slipId || slip._id} จำนวน ${added} รายการ${skipNote} (อัตราจ่ายตามแพ็กเกจปัจจุบัน: ${currentPackageName})`
        : `ดึงโพย ${slip.slipId || slip._id} จำนวน ${added} รายการ${skipNote}`
      toastSuccess(msg)
    } else toast('โพยนี้ไม่มีรายการที่นำมาใช้ได้ในแพ็กเกจปัจจุบัน', { icon: '⚠️' })
  }

  const getPrizeCodeFromName = (name: string): string => {
    const m: Record<string, string> = {
      '3 ตัวบน': 'top3', '3 ตัวล่าง': 'down3', '3 ตัวโต๊ด': 'tod3', '3 ตัวกลับ': 'tod3reverse',
      '2 ตัวบน': 'top2', '2 ตัวล่าง': 'down2', '2 ตัวโต๊ด': 'tod2', '2 ตัวกลับ': 'tod2reverse',
      'วิ่งบน': 'top1', 'วิ่งล่าง': 'down1', 'ตอง': 'top3same', 'เลขปัก': 'top3pick1group', '4-5 ตัว': 'top5fourgroup',
    }
    return m[name] ?? name
  }

  // ประเภทแทงจาก package (เดียวกับ user-classic betTypes + lastRowBetTypes)
  const betTypes = useMemo(() => {
    const defaults = [
      { name: '3 ตัวบน', payout: 1000, color: 'bg_gd_quinary', icon: false },
      { name: '3 ตัวโต๊ด', payout: 150, color: 'bg_gd_quinary', icon: false },
      { name: '3 ตัวกลับ', payout: 0, color: 'bg_gd_quinary', icon: true },
      { name: '2 ตัวบน', payout: 100, color: 'bg_gd_danger', icon: false },
      { name: '2 ตัวล่าง', payout: 100, color: 'bg_gd_danger', icon: false },
      { name: '2 ตัวกลับ', payout: 0, color: 'bg_gd_danger', icon: true },
      { name: '2 ตัวโต๊ด', payout: 13, color: 'bg_gd_danger', icon: false },
      { name: '3 ตัวล่าง', payout: 0, color: 'bg_gd_quinary', icon: false, disabled: true },
      { name: 'ตอง', payout: 1000, color: 'bg_gd_warning', icon: false },
    ]
    if (!usePackage) return defaults.map((d) => ({ ...d, prizeCode: getPrizeCodeFromName(d.name) }))
    return defaults.map((d) => {
      const prizeCode = getPrizeCodeFromName(d.name)
      const rate = rewardMap[prizeCode] ?? 0
      const disabled = d.name === '3 ตัวล่าง' && rate === 0
      return { ...d, prizeCode, payout: rate > 0 ? rate : d.payout, disabled }
    })
  }, [usePackage, rewardMap])

  const lastRowBetTypes = useMemo(() => {
    const defaults = [
      { name: 'วิ่งบน', payout: 3.2, color: 'bg_gd_success' },
      { name: 'วิ่งล่าง', payout: 4.2, color: 'bg_gd_success' },
      { name: 'เลขปัก', payout: 0, color: 'bg_gd_info' },
      { name: '4-5 ตัว', payout: 0, color: 'bg_gd_info' },
    ]
    if (!usePackage) return defaults.map((d) => ({ ...d, prizeCode: getPrizeCodeFromName(d.name) }))
    return defaults.map((d) => {
      const prizeCode = getPrizeCodeFromName(d.name)
      const rate = rewardMap[prizeCode] ?? 0
      return { ...d, prizeCode, payout: rate > 0 ? rate : d.payout }
    })
  }, [usePackage, rewardMap])

  const hasPrizeSelected = selectedPrizeCodes.length > 0
  const hasThreeDigitSelected = selectedPrizeCodes.some((c) => ['top3', 'down3', 'tod3', 'tod3reverse', 'top3same'].includes(c))
  const hasTwoDigitSelected = selectedPrizeCodes.some((c) => ['top2', 'down2', 'tod2', 'tod2reverse'].includes(c))
  const isPickNumberSelected = selectedPrizeCodes.includes('top3pick1group')
  const is45GroupSelected = selectedPrizeCodes.includes('top5fourgroup')
  const currentGroup = selectedPrizeCodes.length ? getGroup(selectedPrizeCodes[0]) : null

  /** โฟกัสช่องกรอกเลขช่องแรก (หลังเลือกประเภทแทง ยกเว้น เลขปัก / 4-5 ตัว) เพื่อให้พิมพ์จาก keyboard ได้เลย */
  const focusFirstNumberInput = useCallback(() => {
    setTimeout(() => firstNumberInputRef.current?.focus(), 100)
  }, [])

  const handlePrizeClick = (prizeCode: string) => {
    const currentGroupOfClick = getGroup(prizeCode)
    const currentSelectedGroup = selectedPrizeCodes.length ? getGroup(selectedPrizeCodes[0]) : null
    const isPukOr45 = (code: string) => code === 'top3pick1group' || code === 'top5fourgroup'

    if (special2DigitType && !selectedPrizeCodes.includes(prizeCode)) setSpecial2DigitType(null)

    if (prizeCode === 'top3same') {
      if (!addBetItem) {
        toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', { icon: '⚠️' })
        return
      }
      const tongNumbersList = Array.from({ length: 10 }, (_, i) => `${i}${i}${i}`)
      const top3Discount = getDiscount('top3')
      tongNumbersList.forEach((number) => {
        const top3PayoutRate = getPayoutRate('top3', number)
        addBetItem({
          number,
          betType: 'top3',
          betTypeLabel: '3 ตัวบน',
          amount: defaultAmount,
          multiplier: top3PayoutRate,
          discount: top3Discount,
        })
      })
      toastSuccess(`เพิ่มรายการเลขตอง (${tongNumbersList.join(', ')}) จำนวน ${tongNumbersList.length} รายการ เรียบร้อยแล้ว`, { duration: 3000 })
      return
    }

    if (['groupPick', 'group45'].includes(currentGroupOfClick)) {
      if (special2DigitType) setSpecial2DigitType(null)
      if (selectedPrizeCodes.includes(prizeCode)) {
        setSelectedPrizeCodes([])
        setSelectedPickNumbers([])
      } else {
        setSelectedPrizeCodes([prizeCode])
        setSelectedPickNumbers([])
      }
      return
    }

    if (currentSelectedGroup && currentGroupOfClick !== currentSelectedGroup) {
      if (special2DigitType) setSpecial2DigitType(null)
      if (prizeCode === 'tod3reverse') setSelectedPrizeCodes(['top3', 'tod3reverse'])
      else if (prizeCode === 'tod2reverse') setSelectedPrizeCodes(['top2', 'tod2reverse'])
      else setSelectedPrizeCodes([prizeCode])
      setSelectedPickNumbers([])
      focusFirstNumberInput()
      return
    }

    if (selectedPrizeCodes.includes(prizeCode)) {
      setSelectedPrizeCodes((prev) => prev.filter((c) => c !== prizeCode))
      return
    }

    if (selectedPrizeCodes.length === 0 && (prizeCode === 'tod3reverse' || prizeCode === 'tod2reverse')) {
      if (special2DigitType) setSpecial2DigitType(null)
      const base = prizeCode === 'tod3reverse' ? 'top3' : 'top2'
      setSelectedPrizeCodes([base, prizeCode])
      focusFirstNumberInput()
      return
    }

    if (special2DigitType && !selectedPrizeCodes.includes(prizeCode)) setSpecial2DigitType(null)
    setSelectedPrizeCodes((prev) => [...new Set([...prev, prizeCode])])
    if (!isPukOr45(prizeCode)) focusFirstNumberInput()
  }

  const handlePickNumberClick = (label: string) => {
    if (selectedPickNumbers.includes(label)) {
      setSelectedPickNumbers((prev) => prev.filter((item) => item !== label))
      return
    }
    if (label === '5 ตัวโต๊ด') {
      setSelectedPickNumbers(['5 ตัวโต๊ด'])
      focusFirstNumberInput()
      return
    }
    if (selectedPickNumbers.includes('5 ตัวโต๊ด') && ['4 ตัวบน', '4 ตัวโต๊ด'].includes(label)) {
      setSelectedPickNumbers([label])
      focusFirstNumberInput()
      return
    }
    setSelectedPickNumbers((prev) => [...prev, label])
    // เลขปัก: เลือก ปักหลักร้อย/สิบ/หน่วย แล้วโฟกัสช่องกรอก | 4-5 ตัว: เลือก 4 ตัวบน/4 ตัวโต๊ด/5 ตัวโต๊ด แล้วโฟกัสช่องกรอก
    focusFirstNumberInput()
  }

  const createSpecial2DigitBetItems = (number: string, type: 'front' | 'back' | '19door') => {
    if (!addBetItem || !hasTwoDigitSelected) return
    let numbers: string[] = []
    if (type === 'front') numbers = Array.from({ length: 10 }, (_, i) => `${number}${i}`)
    else if (type === 'back') numbers = Array.from({ length: 10 }, (_, i) => `${i}${number}`)
    else if (type === '19door') {
      const front = Array.from({ length: 10 }, (_, i) => `${number}${i}`)
      const back = Array.from({ length: 10 }, (_, i) => `${i}${number}`)
      numbers = Array.from(new Set([...front, ...back])).sort()
    }
    const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
    const actualPrizeTypes = selectedPrizeCodes.filter((c) => c !== 'tod2reverse')
    let total = 0
    numbers.forEach((num) => {
      actualPrizeTypes.forEach((prizeCode) => {
        const rate = getPayoutRate(prizeCode, num)
        const discount = getDiscount(prizeCode)
        if (rate <= 0) return
        const label = PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode
        const shouldReverse2 = hasReverse2 && !prizeCode.includes('tod')
        if (shouldReverse2) {
          const permuted = [num, num[1] + num[0]]
          Array.from(new Set(permuted)).forEach((p) => {
            addBetItem!({ number: p, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: getPayoutRate(prizeCode, p), discount })
            total++
          })
        } else {
          addBetItem!({ number: num, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: rate, discount })
          total++
        }
      })
    })
    const typeLabel = type === 'front' ? 'รูดหน้า' : type === 'back' ? 'รูดหลัง' : '19 ประตู'
    toastSuccess(`เพิ่มรายการ${typeLabel} เลข ${number} จำนวน ${total} รายการ${hasReverse2 ? ' (รวมเลขสลับ)' : ''} เรียบร้อยแล้ว`, { duration: 3000 })
    setNumbers?.(Array(5).fill(''))
  }

  const createTwoDigitBetItems = (numberList: string[], description: string) => {
    if (!hasTwoDigitSelected) {
      toast('กรุณาเลือกประเภท 2 ตัวก่อน', { icon: '⚠️' })
      return
    }
    if (!addBetItem || !numberList?.length) {
      toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', { icon: '⚠️' })
      return
    }
    const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
    const actualPrizeTypes = selectedPrizeCodes.filter((c) => c !== 'tod2reverse')
    let total = 0
    numberList.forEach((number) => {
      actualPrizeTypes.forEach((prizeCode) => {
        const rate = getPayoutRate(prizeCode, number)
        const discount = getDiscount(prizeCode)
        if (rate <= 0) return
        const label = PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode
        const shouldReverse2 = hasReverse2 && !prizeCode.includes('tod')
        if (shouldReverse2) {
          const permuted = [number, number[1] + number[0]]
          Array.from(new Set(permuted)).forEach((p) => {
            addBetItem!({ number: p, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: getPayoutRate(prizeCode, p), discount })
            total++
          })
        } else {
          addBetItem!({ number, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: rate, discount })
          total++
        }
      })
    })
    toastSuccess(`เพิ่มรายการ${description} จำนวน ${total} รายการ${hasReverse2 ? ' (รวมเลขสลับ)' : ''} เรียบร้อยแล้ว`, { duration: 3000 })
    setSelectedNumbers(numberList)
  }

  const getRangeNumbers = (rangeStr: string): string[] => {
    const numbers: string[] = []
    const ranges: [number, number][] =
      rangeStr === '000-249' ? [[0, 249]] : rangeStr === '250-499' ? [[250, 499]] : rangeStr === '500-749' ? [[500, 749]] : rangeStr === '750-999' ? [[750, 999]] : rangeStr === '000-499' ? [[0, 499]] : rangeStr === '500-999' ? [[500, 999]] : rangeStr === '000-999' ? [[0, 999]] : []
    ranges.forEach(([a, b]) => {
      for (let i = a; i <= b; i++) numbers.push(i.toString().padStart(3, '0'))
    })
    return numbers
  }

  const handleThreeDigitRangeClick = (range: string) => {
    if (!hasThreeDigitSelected) {
      toast('กรุณาเลือกประเภท 3 ตัวก่อน', { icon: '⚠️' })
      return
    }
    if (!addBetItem) {
      toast('ระบบยังไม่พร้อม กรุณาลองใหม่อีกครั้ง', { icon: '⚠️' })
      return
    }
    const rangeNumbers = getRangeNumbers(range)
    if (rangeNumbers.length === 0) {
      toast('ไม่พบเลขในช่วงที่เลือก', { icon: '⚠️' })
      return
    }
    const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')
    const actualPrizeTypes = selectedPrizeCodes.filter((c) => c !== 'tod3reverse' && c !== 'top3same')
    let total = 0
    rangeNumbers.forEach((number) => {
      actualPrizeTypes.forEach((prizeCode) => {
        const rate = getPayoutRate(prizeCode, number)
        const discount = getDiscount(prizeCode)
        if (rate <= 0) return
        const label = PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode
        const shouldReverse3 = hasReverse3 && !prizeCode.includes('tod')
        if (shouldReverse3) {
          const chars = number.split('')
          const permute = (arr: string[], start: number): void => {
            if (start === arr.length - 1) {
              const p = arr.join('')
              addBetItem!({ number: p, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: getPayoutRate(prizeCode, p), discount })
              total++
              return
            }
            for (let i = start; i < arr.length; i++) {
              const next = [...arr]
              ;[next[start], next[i]] = [next[i], next[start]]
              permute(next, start + 1)
            }
          }
          permute(chars, 0)
        } else {
          addBetItem!({ number, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: rate, discount })
          total++
        }
      })
    })
    toastSuccess(`เพิ่มรายการช่วง ${range} จำนวน ${total} รายการ${hasReverse3 ? ' (รวมเลขสลับ)' : ''} เรียบร้อยแล้ว`, { duration: 3000 })
  }

  /** ปิดโหมดรูดหน้า/รูดหลัง/19 ประตู ก่อนเพิ่มโพยจาก preset (ให้ toast และ UI ตรงกับปุ่มที่กด) */
  const clearSpecial2DigitAndCreate = (numberList: string[], description: string) => {
    setSpecial2DigitType(null)
    createTwoDigitBetItems(numberList, description)
  }
  const handleDoubleDigitClick = () => clearSpecial2DigitAndCreate(Array.from({ length: 10 }, (_, i) => `${i}${i}`.padStart(2, '0')), 'เลขเบิ้ล')
  const handleLow2DigitClick = () => clearSpecial2DigitAndCreate(Array.from({ length: 50 }, (_, i) => i.toString().padStart(2, '0')), 'สองตัวต่ำ (00-49)')
  const handleHigh2DigitClick = () => clearSpecial2DigitAndCreate(Array.from({ length: 50 }, (_, i) => (i + 50).toString().padStart(2, '0')), 'สองตัวสูง (50-99)')
  const handleEven2DigitClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 0 && parseInt(str[1]) % 2 === 0) numbers.push(str)
    }
    clearSpecial2DigitAndCreate(numbers, 'สองตัวคู่')
  }
  const handleOdd2DigitClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 1 && parseInt(str[1]) % 2 === 1) numbers.push(str)
    }
    clearSpecial2DigitAndCreate(numbers, 'สองตัวคี่')
  }
  const handleSiblingNumberClick = () => clearSpecial2DigitAndCreate(Array.from({ length: 9 }, (_, ten) => `${ten}${ten + 1}`), 'พี่น้อง')
  const handleYoungerSiblingClick = () => clearSpecial2DigitAndCreate(Array.from({ length: 9 }, (_, unit) => `${unit + 1}${unit}`), 'น้องพี่')
  const handleOddOddClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 1 && parseInt(str[1]) % 2 === 1) numbers.push(str)
    }
    clearSpecial2DigitAndCreate(numbers, 'เลขคี่คี่')
  }
  const handleEvenEvenClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 0 && parseInt(str[1]) % 2 === 0) numbers.push(str)
    }
    clearSpecial2DigitAndCreate(numbers, 'เลขคู่คู่')
  }
  const handleEvenOddClick = () => {
    const numbers: string[] = []
    for (let i = 0; i < 100; i++) {
      const str = i.toString().padStart(2, '0')
      if (parseInt(str[0]) % 2 === 0 && parseInt(str[1]) % 2 === 1) numbers.push(str)
    }
    clearSpecial2DigitAndCreate(numbers, 'เลขคู่คี่')
  }
  const handleAll2DigitClick = () => clearSpecial2DigitAndCreate(Array.from({ length: 100 }, (_, i) => i.toString().padStart(2, '0')), 'เลข 00-99')

  /**
   * Event delegation: อ่านปุ่มที่ถูกคลิกจาก data-action
   * สำคัญ: ต้องอ่านจาก btn (element ที่หาได้จาก closest) ไม่ใช่ e.target
   * เพราะ e.target อาจเป็น <span> หรือ <div> ข้างในปุ่ม → ไม่มี data-action
   */
  const handleExtra2DigitClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    const btn = target.closest('button[data-action]') as HTMLButtonElement | null
    if (!btn) return
    const action = (btn.dataset.action ?? btn.getAttribute('data-action') ?? '') as string
    if (!action) return
    e.stopPropagation()
    console.log('[รายการเพิ่มเติม 2 ตัว] target:', target.tagName, target.dataset?.action ?? '(ไม่มี)')
    console.log('[รายการเพิ่มเติม 2 ตัว] ปุ่มที่ใช้ (closest):', action)
    const specialMap: Record<string, 'front' | 'back' | '19door'> = { front: 'front', back: 'back', '19door': '19door' }
    if (specialMap[action]) {
      handleSpecial2DigitClick(specialMap[action])
      return
    }
    switch (action) {
      case 'double': handleDoubleDigitClick(); break
      case 'low2': handleLow2DigitClick(); break
      case 'high2': handleHigh2DigitClick(); break
      case 'even2': handleEven2DigitClick(); break
      case 'odd2': handleOdd2DigitClick(); break
      case 'sibling': handleSiblingNumberClick(); break
      case 'younger': handleYoungerSiblingClick(); break
      case 'oddOdd': handleOddOddClick(); break
      case 'evenEven': handleEvenEvenClick(); break
      case 'evenOdd': handleEvenOddClick(); break
      case 'all2': handleAll2DigitClick(); break
      default: break
    }
  }

  const firstNumberInputRef = useRef<HTMLInputElement>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleSpecial2DigitClick = (type: 'front' | 'back' | '19door') => {
    if (!hasTwoDigitSelected) {
      toast('กรุณาเลือกประเภท 2 ตัวก่อน', { icon: '⚠️', position: 'top' })
      return
    }
    setSpecial2DigitType((prev) => (prev === type ? null : type))
    setSelectedPickNumbers([])
    if (type === 'front') {
      toast('รูดหน้า: พิมพ์เลข 1 หลัก จะได้เลข 2 ตัวที่ขึ้นต้นด้วยเลขนั้น', { icon: '⚠️', position: 'top', duration: 3000 })
    } else if (type === 'back') {
      toast('รูดหลัง: พิมพ์เลข 1 หลัก จะได้เลข 2 ตัวที่ลงท้ายด้วยเลขนั้น', { icon: '⚠️', position: 'top', duration: 3000 })
    } else if (type === '19door') {
      toast('19 ประตู: พิมพ์เลข 1 หลัก จะได้เลข 2 ตัวรูดหน้า+หลัง รวม 19 เลข', { icon: '⚠️', position: 'top', duration: 3000 })
    }
    // Focus ช่องกรอกเลขช่องแรก (ตรงกับ user-classic)
    setTimeout(() => firstNumberInputRef.current?.focus(), 100)
  }

  const generatePermutations = useCallback((numberStr: string): string[] => {
    const chars = numberStr.split('')
    const permutations = new Set<string>()
    if (numberStr.length === 2) {
      permutations.add(numberStr)
      permutations.add(chars[1] + chars[0])
    } else if (numberStr.length === 3) {
      const permute = (arr: string[], start: number = 0) => {
        if (start === arr.length - 1) { permutations.add(arr.join('')); return }
        for (let i = start; i < arr.length; i++) {
          ;[arr[start], arr[i]] = [arr[i], arr[start]]
          permute([...arr], start + 1)
          ;[arr[start], arr[i]] = [arr[i], arr[start]]
        }
      }
      permute(chars)
    } else permutations.add(numberStr)
    return Array.from(permutations).sort()
  }, [])

  const checkAndSubmitBet = useCallback(() => {
    if (!addBetItem || !currentNumber) return
    if (isPickNumberSelected && selectedPickNumbers.length === 0) {
      toast('กรุณาเลือกตำแหน่งการปัก (ปักหลักร้อย, ปักหลักสิบ, หรือ ปักหลักหน่วย) ก่อนส่งโพย', { position: 'top', duration: 4000, icon: '⚠️' })
      return
    }
    if (is45GroupSelected && selectedPickNumbers.length === 0) {
      toast('กรุณาเลือกประเภทการแทง (4 ตัวบน, 4 ตัวโต๊ด, หรือ 5 ตัวโต๊ด) ก่อนส่งโพย', { position: 'top', duration: 4000, icon: '⚠️' })
      return
    }

    if (isPickNumberSelected && selectedPickNumbers.length > 0) {
      const pickMap: Record<string, string> = { 'ปักหลักร้อย': 'puk100', 'ปักหลักสิบ': 'puk10', 'ปักหลักหน่วย': 'puk1' }
      selectedPickNumbers.forEach((pickType) => {
        const prizeCode = pickMap[pickType]
        if (!prizeCode) return
        const rate = getPayoutRate(prizeCode, currentNumber)
        if (rate > 0) addBetItem({ number: currentNumber, betType: prizeCode, betTypeLabel: pickType, amount: defaultAmount, multiplier: rate, discount: getDiscount(prizeCode) })
      })
    } else if (is45GroupSelected && selectedPickNumbers.length > 0) {
      const pickMap: Record<string, string> = { '4 ตัวบน': 'top4', '4 ตัวโต๊ด': 'tod4', '5 ตัวโต๊ด': 'tod5' }
      selectedPickNumbers.forEach((pickType) => {
        const prizeCode = pickMap[pickType]
        if (!prizeCode) return
        const rate = getPayoutRate(prizeCode, currentNumber)
        if (rate > 0) addBetItem({ number: currentNumber, betType: prizeCode, betTypeLabel: pickType, amount: defaultAmount, multiplier: rate, discount: getDiscount(prizeCode) })
      })
    } else {
      const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')
      const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
      const actualPrizeTypes = selectedPrizeCodes.filter((c) => c !== 'tod3reverse' && c !== 'tod2reverse' && c !== 'top3same')
      let total = 0
      actualPrizeTypes.forEach((prizeCode) => {
        const rate = getPayoutRate(prizeCode, currentNumber)
        const discount = getDiscount(prizeCode)
        if (rate <= 0) return
        const shouldReverse3 = hasReverse3 && !prizeCode.includes('tod') && currentNumber.length === 3
        const shouldReverse2 = hasReverse2 && !prizeCode.includes('tod') && currentNumber.length === 2
        if (shouldReverse3 || shouldReverse2) {
          generatePermutations(currentNumber).forEach((p) => {
            addBetItem!({ number: p, betType: prizeCode, betTypeLabel: PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode, amount: defaultAmount, multiplier: getPayoutRate(prizeCode, p), discount })
            total++
          })
        } else {
          addBetItem!({ number: currentNumber, betType: prizeCode, betTypeLabel: PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode, amount: defaultAmount, multiplier: rate, discount })
          total++
        }
      })
      if (total > 0) toastSuccess(`เพิ่มรายการเดิมพัน ${currentNumber} จำนวน ${total} รายการ${(hasReverse3 || hasReverse2) ? ' (รวมเลขสลับ)' : ''} เรียบร้อยแล้ว`, { duration: 2000 })
    }

    setNumbers?.(Array(5).fill(''))
    setTimeout(() => firstNumberInputRef.current?.focus(), 50)
  }, [addBetItem, currentNumber, defaultAmount, isPickNumberSelected, is45GroupSelected, selectedPickNumbers, selectedPrizeCodes, setNumbers, generatePermutations])

  const handleSubmitFromPanel = () => {
    if (!addBetItem || selectedNumbers.length === 0) {
      toast('กรุณาเลือกตัวเลขจากแผงก่อน', { icon: '⚠️' })
      return
    }
    if (!hasPrizeSelected) {
      toast('กรุณาเลือกประเภทการแทงก่อน', { icon: '⚠️' })
      return
    }
    const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
    const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')
    const actualPrizeTypes = selectedPrizeCodes.filter((c) => c !== 'tod2reverse' && c !== 'tod3reverse' && c !== 'top3same')
    let total = 0
    selectedNumbers.forEach((number) => {
      actualPrizeTypes.forEach((prizeCode) => {
        const rate = getPayoutRate(prizeCode, number)
        const discount = getDiscount(prizeCode)
        if (rate <= 0) return
        const label = PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode
        const shouldReverse = (hasReverse2 && number.length === 2 && !prizeCode.includes('tod')) || (hasReverse3 && number.length === 3 && !prizeCode.includes('tod'))
        if (shouldReverse) {
          const permuted = generatePermutations(number)
          permuted.forEach((p) => { addBetItem!({ number: p, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: getPayoutRate(prizeCode, p), discount }); total++ })
        } else {
          addBetItem!({ number, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: rate, discount })
          total++
        }
      })
    })
    toastSuccess(`เพิ่มรายการจากแผง จำนวน ${total} รายการ${(hasReverse2 || hasReverse3) ? ' (รวมเลขสลับ)' : ''} เรียบร้อยแล้ว`, { duration: 3000 })
    setSelectedNumbers([])
    setShowNumberPanel(false)
    setNumbers?.(Array(5).fill(''))
  }

  const handleClearLastDigit = () => {
    backspace()
    setTimeout(() => firstNumberInputRef.current?.focus(), 50)
  }

  /** เมื่อกดเลข ถ้าเปิดโหมดรูดหน้า/หลัง/19ประตู จะเพิ่มโพยทันที ไม่ใช่แค่ใส่ตัวเลข */
  const handleAppendDigit = (d: number) => {
    if (special2DigitType) {
      createSpecial2DigitBetItems(String(d), special2DigitType)
      return
    }
    appendDigit(d)
  }

  const prevInputLenRef = useRef(0)
  /** เมื่อกรอกครบ inputCount หลัก ให้ส่งโพยอัตโนมัติ (เหมือน user-classic) */
  useEffect(() => {
    const len = currentNumber.length
    if (len === inputCount && inputCount > 0 && prevInputLenRef.current < inputCount) {
      if (!addBetItem) return
      if (isPickNumberSelected && selectedPickNumbers.length === 0) return
      if (is45GroupSelected && selectedPickNumbers.length === 0) return
      checkAndSubmitBet()
    }
    prevInputLenRef.current = len
  }, [currentNumber, inputCount, addBetItem, isPickNumberSelected, is45GroupSelected, selectedPickNumbers.length, checkAndSubmitBet])

  /** สรุปประเภทที่เลือก (หลายประเภทได้ เหมือน user-classic) */
  const renderSelectedSummary = () => {
    if (!hasPrizeSelected) {
      return <h5 className="c_tertiary text-center">-- กรุณาเลือกหวยที่จะแทง --</h5>
    }
    return (
      <div className="box_row flex-wrap justify-content-center" style={{ gap: 4, margin: '0 -2px !important' as React.CSSProperties['margin'] }}>
        {selectedPrizeCodes.map((code) => (
          <div key={code} className="font_rg bg_secondary border_5 px-1 py-0">
            <span className="c_secondary">{PRIZE_CODE_NAME_MAP[code] ?? code}</span>
          </div>
        ))}
        {selectedPickNumbers.map((label) => (
          <div key={label} className="font_rg bg_tertiary border_5 px-1 py-0">
            <span className="c_secondary">{label}</span>
          </div>
        ))}
      </div>
    )
  }

  const renderBetTypeLegacy = (t: (typeof BET_TYPES_MAIN)[0], colClass: string) => {
    const prizeCode = LEGACY_ID_TO_PRIZE_CODE[t.id] ?? t.id
    const selected = !t.disabled && selectedPrizeCodes.includes(prizeCode)
    const isCenterLabel = t.label === 'เลขปัก' || t.label === '4-5 ตัว'
    const innerClass = [
      'link_select', selected ? 'selected_link' : '', t.disabled ? 'link_disable' : '',
      'box_row position-relative font_rg', isCenterLabel ? 'justify-content-center' : '', t.bgClass, 'border_5',
    ].filter(Boolean).join(' ')
    return (
      <div key={t.id} className={`${colClass} huay_bet_col`} style={COL_PAD}>
        <div role="button" tabIndex={0} className={innerClass} onClick={() => !t.disabled && handlePrizeClick(prizeCode)} onKeyDown={(e) => e.key === 'Enter' && !t.disabled && handlePrizeClick(prizeCode)}>
          {t.label === 'เลขปัก' || t.label === '4-5 ตัว' ? (
            <span className="c_tertiary">{t.label}</span>
          ) : (
            <>
              <span className="c_tertiary ps-2">{t.label}</span>
              <div className="box_price">
                <span className="c_secondary" style={t.price === '⇄' ? { fontSize: 14 } : undefined}>{t.price}</span>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  const renderBetTypeFromPackage = (item: { name: string; prizeCode: string; payout: number; color: string; icon?: boolean; disabled?: boolean }, colClass: string) => {
    const rate = getPayoutRate(item.prizeCode)
    const enabled = !item.disabled && rate > 0
    const isReverse = item.name === '3 ตัวกลับ' || item.name === '2 ตัวกลับ'
    const selected = selectedPrizeCodes.includes(item.prizeCode)
    const isCenterLabel = item.name === 'เลขปัก' || item.name === '4-5 ตัว'
    const innerClass = [
      'link_select', selected ? 'selected_link' : '', !enabled && !isReverse ? 'link_disable' : '',
      'box_row position-relative font_rg', isCenterLabel ? 'justify-content-center' : '', item.color, 'border_5',
    ].filter(Boolean).join(' ')
    const displayPrice = isReverse ? '⇄' : rate > 0 ? rate.toLocaleString('th-TH') : '-'
    return (
      <div key={item.prizeCode} className={`${colClass} huay_bet_col`} style={COL_PAD}>
        <div
          role="button"
          tabIndex={0}
          className={innerClass}
          onClick={() => (enabled || isReverse) && handlePrizeClick(item.prizeCode)}
          onKeyDown={(e) => e.key === 'Enter' && (enabled || isReverse) && handlePrizeClick(item.prizeCode)}
        >
          {isCenterLabel ? (
            <span className="c_tertiary">{item.name}</span>
          ) : (
            <>
              <span className="c_tertiary ps-2">{item.name}</span>
              <div className="box_price">
                <span className="c_secondary" style={displayPrice === '⇄' ? { fontSize: 14 } : undefined}>{displayPrice}</span>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="col-12 col-lg-6 px-1">
      <div className="d-grid gap-2">
        {/* ตรง HTML 235-247: h_box p-0, ลูก 1 = d-flex align-items-center (logo+ชื่อ), ลูก 2 = countdown */}
        <div className="h_box p-0">
          <div className="d-flex align-items-center">
            <div className="square_hi bx_sdp">
              <img src={logo} alt="" />
            </div>
            <h6 className="font_md ms-2 c_tertiary">{roundLabel ? `${title} · ${roundLabel}` : title}</h6>
          </div>
          <div className="bg_secondary border_5 px-2 py-1 ms-auto c_primary box_row justify-content-center">
            <h6>{countdown}</h6>
          </div>
        </div>

        {/* ตรง HTML 249-258: เตือนเลขปรับราคา — div class="" style="width: 14px" (HTML ใช้ img warning.png), small */}
        <div className="border_5 box_row flex-nowrap gap-1 bdc_primary bg_op_secondary p-2 overflow-hidden">
          <div className="" style={{ width: 14 }}>
            <img src={logo.replace(/[^/]+$/, 'warning.png')} alt="" />
          </div>
          <small className="c_tertiary">มีเลขปรับราคาจ่าย ให้ตรวจสอบราคาจ่ายทุกครั้ง</small>
        </div>

        {/* แถวประเภทแทง — จาก package (rewardMap) หรือ fallback BET_TYPES_MAIN ตรงกับ user-classic */}
        <div className="row huay_bet_row" style={ROW_STYLE}>
          {usePackage ? (
            <>
              {betTypes.slice(0, 3).map((t) => renderBetTypeFromPackage(t, 'col-4'))}
              {betTypes.slice(3, 6).map((t) => renderBetTypeFromPackage(t, 'col-4'))}
              {betTypes.slice(6, 9).map((t) => renderBetTypeFromPackage(t, 'col-4'))}
              {lastRowBetTypes.map((t) => renderBetTypeFromPackage(t, 'col-3'))}
            </>
          ) : (
            <>
              {BET_TYPES_MAIN.slice(0, 3).map((t) => renderBetTypeLegacy(t, 'col-4'))}
              {BET_TYPES_MAIN.slice(3, 6).map((t) => renderBetTypeLegacy(t, 'col-4'))}
              {BET_TYPES_MAIN.slice(6, 9).map((t) => renderBetTypeLegacy(t, 'col-4'))}
              {BET_TYPES_MAIN.slice(9, 14).map((t) => renderBetTypeLegacy(t, 'col-3'))}
            </>
          )}
        </div>

        {/* รายการเพิ่มเติม 3 ตัว — แสดงเฉพาะเมื่อเลือกกลุ่ม 3 ตัว */}
        {hasThreeDigitSelected && (
          <div className="border_5 d-grid gap-2 bdc_primary bg_op_secondary p-2 overflow-hidden">
            <h6 className="bg_gd_quinary c_tertiary rounded-1 text-center px-2 mx-auto" style={{ width: 'fit-content' }}>
              รายการเพิ่มเติม 3 ตัว
            </h6>
            <div className="row huay_bet_row" style={ROW_STYLE}>
              {['000-249', '250-499', '500-749', '750-999'].map((l) => (
                <div key={l} className="col-3 huay_bet_col" style={COL_PAD}>
                  <div role="button" tabIndex={0} className="link_select box_row position-relative justify-content-center font_rg bg_secondary border_5 hh_hover" onClick={() => handleThreeDigitRangeClick(l)} onKeyDown={(e) => e.key === 'Enter' && handleThreeDigitRangeClick(l)}>
                    <span className="c_secondary">{l}</span>
                  </div>
                </div>
              ))}
              {['000-499', '500-999'].map((l) => (
                <div key={l} className="col-6 huay_bet_col" style={COL_PAD}>
                  <div role="button" tabIndex={0} className="link_select box_row position-relative justify-content-center font_rg bg_secondary border_5 hh_hover" onClick={() => handleThreeDigitRangeClick(l)} onKeyDown={(e) => e.key === 'Enter' && handleThreeDigitRangeClick(l)}>
                    <span className="c_secondary">{l}</span>
                  </div>
                </div>
              ))}
              <div className="col-12 huay_bet_col" style={COL_PAD}>
                <div role="button" tabIndex={0} className="link_select box_row position-relative justify-content-center font_rg bg_secondary border_5 hh_hover" onClick={() => handleThreeDigitRangeClick('000-999')} onKeyDown={(e) => e.key === 'Enter' && handleThreeDigitRangeClick('000-999')}>
                  <span className="c_secondary">000-999</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* รายการเพิ่มเติม 2 ตัว — แต่ละ col ใส่ position:relative + zIndex เรียงกัน เพื่อให้ปุ่มแถวล่างอยู่ชั้นบน (แก้กรณีกดพี่น้องแต่ได้ low2) */}
        {hasTwoDigitSelected && (() => {
          const extra2Items: { action: string; label: string; colClass: string }[] = [
            { action: 'front', label: 'รูดหน้า', colClass: 'col-3' },
            { action: 'back', label: 'รูดหลัง', colClass: 'col-3' },
            { action: '19door', label: '19 ประตู', colClass: 'col-3' },
            { action: 'double', label: 'เลขเบิ้ล', colClass: 'col-3' },
            { action: 'low2', label: 'สองตัวต่ำ', colClass: 'col-3' },
            { action: 'high2', label: 'สองตัวสูง', colClass: 'col-3' },
            { action: 'even2', label: 'สองตัวคู่', colClass: 'col-3' },
            { action: 'odd2', label: 'สองตัวคี่', colClass: 'col-3' },
            { action: 'sibling', label: 'พี่น้อง', colClass: 'col-3' },
            { action: 'younger', label: 'น้องพี่', colClass: 'col-3' },
            { action: 'oddOdd', label: 'เลขคี่คี่', colClass: 'col-3' },
            { action: 'evenEven', label: 'เลขคู่คู่', colClass: 'col-3' },
            { action: 'evenOdd', label: 'เลขคู่คี่', colClass: 'col-6' },
            { action: 'all2', label: 'เลข 00-99', colClass: 'col-6' },
          ]
          return (
            <div className="border_5 bdc_primary bg_op_secondary p-2 overflow-hidden">
              <h6 className="bg_gd_danger c_tertiary rounded-1 text-center px-2 mx-auto mb-2" style={{ width: 'fit-content' }}>
                รายการเพิ่มเติม 2 ตัว
              </h6>
              <div className="row huay_bet_row" style={ROW_STYLE} onClick={handleExtra2DigitClick} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).closest('button[data-action]')) handleExtra2DigitClick(e as unknown as React.MouseEvent); }}>
                {extra2Items.map((item, index) => {
                  const isActive = ['front', 'back', '19door'].includes(item.action) && special2DigitType === item.action
                  return (
                    <div key={item.action} className={`${item.colClass} huay_bet_col`} style={{ ...COL_PAD, position: 'relative' as const, zIndex: index + 1 }}>
                      <button
                        type="button"
                        data-action={item.action}
                        className={`w-100 border-0 link_select box_row justify-content-center font_rg border_5 hh_hover ${isActive ? 'selected_link' : ''}`}
                      >
                        <div
                          className="box_row justify-content-center ps-1 border_5 w-100 bg_secondary"
                          style={isActive ? { border: '2px solid var(--color-primary)' } : undefined}
                        >
                          <span className="me-0 c_secondary">{item.label}</span>
                        </div>
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {/* รายการเพิ่มเติม เลขปัก — ใส่ zIndex เรียงกันเหมือนรายการ 2 ตัว เพื่อไม่ให้กดผิดปุ่ม */}
        {isPickNumberSelected && (
          <div className="border_5 bdc_primary bg_op_secondary p-2 overflow-hidden">
            <h6 className="bg_gd_info c_tertiary rounded-1 text-center px-2 mx-auto mb-2" style={{ width: 'fit-content' }}>
              รายการเพิ่มเติม เลขปัก
            </h6>
            <div className="row huay_bet_row" style={ROW_STYLE}>
              {['ปักหลักร้อย', 'ปักหลักสิบ', 'ปักหลักหน่วย'].map((l, i) => (
                <div key={l} className="col-4 huay_bet_col" style={{ ...COL_PAD, position: 'relative' as const, zIndex: i + 1 }}>
                  <div role="button" tabIndex={0} className={`link_select box_row justify-content-center font_rg border_5 hh_hover ${selectedPickNumbers.includes(l) ? 'selected_link' : ''} bg_secondary`} onClick={() => handlePickNumberClick(l)} onKeyDown={(e) => e.key === 'Enter' && handlePickNumberClick(l)}>
                    <span className="c_secondary">{l}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* รายการเพิ่มเติม 4-5 ตัว — ใส่ zIndex เรียงกันเหมือนรายการ 2 ตัว เพื่อไม่ให้กดผิดปุ่ม */}
        {is45GroupSelected && (
          <div className="border_5 bdc_primary bg_op_secondary p-2 overflow-hidden">
            <h6 className="bg_gd_info c_tertiary rounded-1 text-center px-2 mx-auto mb-2" style={{ width: 'fit-content' }}>
              รายการเพิ่มเติม 4-5 ตัว
            </h6>
            <div className="row huay_bet_row" style={ROW_STYLE}>
              {['4 ตัวบน', '4 ตัวโต๊ด', '5 ตัวโต๊ด'].map((l, i) => (
                <div key={l} className="col-4 huay_bet_col" style={{ ...COL_PAD, position: 'relative' as const, zIndex: i + 1 }}>
                  <div role="button" tabIndex={0} className={`link_select box_row justify-content-center font_rg border_5 hh_hover ${selectedPickNumbers.includes(l) ? 'selected_link' : ''} bg_secondary`} onClick={() => handlePickNumberClick(l)} onKeyDown={(e) => e.key === 'Enter' && handlePickNumberClick(l)}>
                    <span className="c_secondary">{l}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* รายการที่เลือก — แสดงประเภทการแทงที่เลือก (ตรงกับ user-classic renderSelectedSummary) */}
        <div className="light_sd border_5 d-grid gap-2 bdc_primary bg_op_secondary p-2 overflow-hidden">
          <h6 className="bg_primary c_tertiary rounded-1 text-center px-2 mx-auto" style={{ width: 'fit-content' }}>
            รายการที่เลือก
          </h6>
          {renderSelectedSummary()}
        </div>

        {/* แผงเลข 0-9 สำหรับรูดหน้า/รูดหลัง/19 ประตู — แสดงเฉพาะเมื่อเลือกประเภท 2 ตัว และกดเลือกโหมด (รูดหน้า/รูดหลัง/19 ประตู) แล้ว */}
        {hasTwoDigitSelected && special2DigitType !== null && (
          <div className="border_5 d-grid gap-2 bdc_primary bg_op_secondary p-2 overflow-hidden">
            <h6 className="bg_gd_danger c_tertiary rounded-1 text-center px-2 mx-auto" style={{ width: 'fit-content' }}>
              {special2DigitType === 'front' ? 'รูดหน้า' : special2DigitType === 'back' ? 'รูดหลัง' : '19 ประตู'}
              <span className="ms-2 font_rg c_tertiary">— กดเลข 1 หลัก เพื่อเพิ่มโพย</span>
            </h6>
            <div className="box_row_number" style={{ margin: '0 -2px !important' as React.CSSProperties['margin'] }}>
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <div key={n} className="huay_bet_col" style={COL_PAD}>
                  <div className="link_select box_row position-relative justify-content-center font_rg bg_op_primary border_5 hh_hover" role="button" tabIndex={0} onClick={() => handleAppendDigit(n)} onKeyDown={(e) => e.key === 'Enter' && handleAppendDigit(n)}>
                    <span className="c_tertiary">{n}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ระบุตัวเลข + แป้นตัวเลข + ปุ่ม */}
        <div className="d-grid gap-3">
          <h3 className="text-center c_tertiary font_md">
            ระบุตัวเลข
            {special2DigitType && (
              <span className="ms-2">
                ({special2DigitType === 'front' && 'รูดหน้า'}
                {special2DigitType === 'back' && 'รูดหลัง'}
                {special2DigitType === '19door' && '19 ประตู'}
                <button type="button" className="btn btn-sm btn-outline-warning ms-2" onClick={() => setSpecial2DigitType(null)} title="ยกเลิกโหมดพิเศษ">
                  <i className="fas fa-times" />
                </button>
              </span>
            )}
          </h3>
          <div className="huay_fields justify-content-center">
            {/* โหมดรูดหน้า/รูดหลัง/19 ประตู ใช้ 1 ช่องเท่านั้น (ตรงกับ user-classic displayNumbers) */}
            {Array.from({ length: special2DigitType ? 1 : Math.max(1, inputCount) }, (_, i) => i).map((i) => {
              const totalInputs = special2DigitType ? 1 : Math.max(1, inputCount)
              return (
                <div key={i} className="huay_fields_item">
                  <input
                    ref={(el) => {
                      if (el) {
                        inputRefs.current[i] = el
                        if (i === 0) (firstNumberInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el
                      }
                    }}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={numbers[i] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 1)
                      if (special2DigitType && i === 0 && v) {
                        createSpecial2DigitBetItems(v, special2DigitType)
                        setNumbers?.(Array(5).fill(''))
                        firstNumberInputRef.current?.focus()
                        return
                      }
                      handleNumberInput(i, v)
                      if (v && i + 1 < totalInputs) setTimeout(() => inputRefs.current[i + 1]?.focus(), 0)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !(numbers[i] ?? '') && i > 0) {
                        inputRefs.current[i - 1]?.focus()
                      }
                    }}
                    disabled={!hasPrizeSelected}
                  />
                </div>
              )
            })}
          </div>
          <div className="row huay_bet_row" style={ROW_STYLE}>
            <div className="col-8 huay_bet_col" style={COL_PAD}>
              <div className="row huay_bet_row" style={ROW_STYLE}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <div key={n} className="col-4 huay_bet_col" style={COL_PAD}>
                    <div className="hh_hover bg_secondary bdc_primary ins_sd border_5 py-1 h_num" role="button" tabIndex={0} onClick={() => handleAppendDigit(n)} onKeyDown={(e) => e.key === 'Enter' && handleAppendDigit(n)}>
                      <h3 className="c_secondary font_rg text-center">{n}</h3>
                    </div>
                  </div>
                ))}
                <div className="col-12 huay_bet_col" style={COL_PAD}>
                  <div className="hh_hover bg_secondary bdc_primary ins_sd border_5 py-1 h_num" role="button" tabIndex={0} onClick={() => handleAppendDigit(0)} onKeyDown={(e) => e.key === 'Enter' && handleAppendDigit(0)}>
                    <h3 className="c_secondary font_rg text-center">0</h3>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-4 huay_bet_col" style={COL_PAD}>
              <div className="row huay_bet_row" style={ROW_STYLE}>
                <div className="col-12 huay_bet_col" style={COL_PAD}>
                  <button type="button" className="hh_hover bg_gd_secondary bdc_primary ins_sd border_5 py-1 w-100 h_num" onClick={handleClearLastDigit}>
                    <h4 className="c_tertiary font_rg text-center"><i className="fas fa-backspace" /></h4>
                  </button>
                </div>
                <div className="col-12 huay_bet_col" style={COL_PAD}>
                  <button type="button" className="hh_hover bg_gd_tertiary bdc_primary ins_sd border_5 py-1 w-100 h_num" onClick={() => setShowNumberSetModal(true)}>
                    <h6 className="text-center font_md" style={{ color: '#fff' }}>เลือกเลขชุด</h6>
                  </button>
                </div>
                <div className="col-12 huay_bet_col" style={COL_PAD}>
                  <button type="button" className="hh_hover bg_gd_quaternary bdc_primary ins_sd border_5 py-1 w-100 h_num" onClick={() => setShowOldSlipModal(true)}>
                    <h6 className="text-center font_md" style={{ color: '#fff' }}>ดึงโพยเก่า</h6>
                  </button>
                </div>
                <div className="col-12 huay_bet_col" style={COL_PAD}>
                  <button
                    type="button"
                    className="hh_hover bg_gd_primary bdc_primary ins_sd border_5 py-1 w-100 h_num"
                    disabled={!hasPrizeSelected || isPickNumberSelected || is45GroupSelected}
                    onClick={() => {
                      if (!hasPrizeSelected) { toast('กรุณาเลือกประเภทการแทงก่อน', { icon: '⚠️' }); return }
                      if (isPickNumberSelected) { toast('ประเภทนี้ไม่สามารถเลือกจากแผงได้', { icon: '⚠️' }); return }
                      if (is45GroupSelected) { toast('ประเภท 4-5 ตัวไม่สามารถเลือกจากแผงได้', { icon: '⚠️' }); return }
                      setShowNumberPanel((prev) => !prev)
                    }}
                  >
                    <h6 className="c_tertiary text-center">{showNumberPanel ? 'ปิดแผง' : 'เลือกจากแผง'}</h6>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* แผงตัวเลข — แสดงเมื่อเปิด "เลือกจากแผง" และเลือกกลุ่ม 3/2/1 ตัว */}
        {showNumberPanel && (currentGroup === 'group3' || currentGroup === 'group2' || currentGroup === 'groupRun') && (
          <div className="border_5 d-grid gap-2 bdc_primary bg_op_secondary p-2 overflow-hidden">
            <h6 className="bg_primary c_tertiary rounded-1 text-center px-2 mx-auto" style={{ width: 'fit-content' }}>
              เลือกจากแผง
            </h6>
            {currentGroup === 'group3' && (
              <>
                <div className="nav nav-pills nav_pills_number" role="tablist">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => {
                    const isRangeActive = panel3Active === i
                    return (
                      <button key={i} type="button" className={`nav-link ${isRangeActive ? 'active' : ''}`} onClick={() => setPanel3Active(i)} style={isRangeActive ? { color: '#fff', fontWeight: 700 } : undefined}>
                        {String(i * 100).padStart(3, '0')}
                      </button>
                    )
                  })}
                </div>
                <div className="select_number d-grid gap-2">
                  {Array.from({ length: 100 }, (_, j) => String(panel3Active * 100 + j).padStart(3, '0')).map((num) => {
                    const isSelected = selectedNumbersFromBetItems.has(num)
                    return (
                      <div
                        key={num}
                        className={`hh_hover ins_sd border_5 py-1 h_num ${isSelected ? 'bg_gd_primary' : 'bg_secondary'} bdc_primary`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (!addBetItem || !hasPrizeSelected) { toast('กรุณาเลือกประเภทการแทงก่อน', { icon: '⚠️' }); return }
                          if (isPickNumberSelected && selectedPickNumbers.length === 0) { toast('กรุณาเลือกตำแหน่งการปักก่อน', { icon: '⚠️' }); return }
                          if (is45GroupSelected && selectedPickNumbers.length === 0) { toast('กรุณาเลือกประเภท 4-5 ตัวก่อน', { icon: '⚠️' }); return }
                          const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
                          const hasReverse3 = selectedPrizeCodes.includes('tod3reverse')
                          const actual = selectedPrizeCodes.filter((c) => c !== 'tod2reverse' && c !== 'tod3reverse' && c !== 'top3same')
                          actual.forEach((prizeCode) => {
                            const rate = getPayoutRate(prizeCode, num)
                            const discount = getDiscount(prizeCode)
                            if (rate <= 0) return
                            const label = PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode
                            const shouldReverse = (hasReverse2 && num.length === 2 && !prizeCode.includes('tod')) || (hasReverse3 && num.length === 3 && !prizeCode.includes('tod'))
                            if (shouldReverse) {
                              generatePermutations(num).forEach((p) => addBetItem!({ number: p, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: getPayoutRate(prizeCode, p), discount }))
                            } else addBetItem!({ number: num, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: rate, discount })
                          })
                          toastSuccess(`เพิ่มรายการ ${num} เรียบร้อยแล้ว`, { duration: 2000 })
                        }}
                      >
                        <h5 className={`text-center ${!isSelected ? 'c_secondary' : ''}`} style={isSelected ? { color: '#fff', fontWeight: 700 } : undefined}>{num}</h5>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
            {currentGroup === 'group2' && (
              <div className="select_number d-grid gap-2">
                {Array.from({ length: 100 }, (_, i) => i.toString().padStart(2, '0')).map((num) => {
                  const isSelected = selectedNumbersFromBetItems.has(num)
                  return (
                    <div
                      key={num}
                      className={`hh_hover ins_sd border_5 py-1 h_num ${isSelected ? 'bg_gd_primary' : 'bg_secondary'} bdc_primary`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (!addBetItem || !hasPrizeSelected) { toast('กรุณาเลือกประเภทการแทงก่อน', { icon: '⚠️' }); return }
                        const hasReverse2 = selectedPrizeCodes.includes('tod2reverse')
                        const actual = selectedPrizeCodes.filter((c) => c !== 'tod2reverse' && c !== 'tod3reverse' && c !== 'top3same')
                        actual.forEach((prizeCode) => {
                          const rate = getPayoutRate(prizeCode, num)
                          const discount = getDiscount(prizeCode)
                          if (rate <= 0) return
                          const label = PRIZE_CODE_NAME_MAP[prizeCode] || prizeCode
                          if (hasReverse2 && !prizeCode.includes('tod')) {
                            generatePermutations(num).forEach((p) => addBetItem!({ number: p, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: getPayoutRate(prizeCode, p), discount }))
                          } else addBetItem!({ number: num, betType: prizeCode, betTypeLabel: label, amount: defaultAmount, multiplier: rate, discount })
                        })
                        toastSuccess(`เพิ่มรายการ ${num} เรียบร้อยแล้ว`, { duration: 2000 })
                      }}
                    >
                      <h5 className={`text-center ${!isSelected ? 'c_secondary' : ''}`} style={isSelected ? { color: '#fff', fontWeight: 700 } : undefined}>{num}</h5>
                    </div>
                  )
                })}
              </div>
            )}
            {currentGroup === 'groupRun' && (
              <div className="select_number d-grid gap-2">
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <div key={n} className="hh_hover bg_secondary bdc_primary ins_sd border_5 py-1 h_num" role="button" tabIndex={0} onClick={() => handleAppendDigit(n)}>
                    <h5 className="c_secondary text-center">{n}</h5>
                  </div>
                ))}
              </div>
            )}
            <button type="button" className="link_pmr ins_sd box_row justify-content-center font_rg bg_gd_quaternary border_10 py-2" onClick={handleSubmitFromPanel}>
              <span className="ft_sd c_tertiary font_md"><i className="fas fa-check me-2" />เลือกจากแผงเสร็จกดที่นี่เพื่อใส่ราคา</span>
            </button>
          </div>
        )}

        {/* Modal เลือกเลขชุด */}
        {showNumberSetModal && (
          <>
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-75" style={{ zIndex: 1040 }} onClick={() => setShowNumberSetModal(false)} aria-hidden />
            <div className="position-fixed start-50 translate-middle-x bg-white border rounded shadow overflow-auto" style={{ zIndex: 1050, width: 'min(480px, calc(100vw - 24px))', maxHeight: 'calc(100vh - 24px)' }} role="dialog">
              <div className="modal-header border-bottom">
                <h5 className="modal-title c_tertiary">เลือกเลขชุด</h5>
                <button type="button" className="btn-close" onClick={() => setShowNumberSetModal(false)} aria-label="ปิด" />
              </div>
              <div className="modal-body p-0 overflow-auto">
                <table className="table table-striped table-bordered text-center mb-0">
                  <thead className="table-dark"><tr><th>ลำดับ</th><th>ชื่อเลขชุด</th><th>เมื่อ</th><th></th></tr></thead>
                  <tbody>
                    {loadingNumberSets ? (
                      <tr><td colSpan={4} className="text-center text-muted py-3">กำลังโหลด...</td></tr>
                    ) : numberSetList.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-muted py-3">ยังไม่มีเลขชุด กรุณาไปสร้างที่หน้าจัดการเลขชุด</td></tr>
                    ) : (
                      numberSetList.map((set, index) => (
                        <tr key={set._id}>
                          <td>{index + 1}.</td>
                          <td>{set.name}</td>
                          <td>{set.updatedAt ? new Date(set.updatedAt).toLocaleString('th-TH', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                          <td><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleApplyNumberSet(set)}>คลิกเลือก</button></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer justify-content-center border-top">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNumberSetModal(false)}>ปิด</button>
              </div>
            </div>
          </>
        )}

        {/* Modal ดึงโพยเก่า — auto-fit ตามเนื้อหา */}
        {showOldSlipModal && (
          <>
            <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark opacity-75" style={{ zIndex: 1040 }} onClick={() => setShowOldSlipModal(false)} aria-hidden />
            <div
              className="position-fixed start-50 bg-white border rounded shadow old-slip-modal-modern"
              style={{
                zIndex: 1050,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 'fit-content',
                minWidth: 'min(560px, calc(100vw - 24px))',
                maxWidth: 'min(920px, calc(100vw - 24px))',
                maxHeight: 'calc(100vh - 24px)',
                borderRadius: '0.75rem',
                boxShadow: '0 1rem 2.5rem rgba(0, 0, 0, 0.18)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
              role="dialog"
            >
              <div className="modal-header border-bottom">
                <h5 className="modal-title c_tertiary">ดึงโพยเก่า</h5>
                <button type="button" className="btn-close" onClick={() => setShowOldSlipModal(false)} aria-label="ปิด" />
              </div>
              {!loadingOldSlips && oldSlipList.length > 0 && selectedPackage?.code && (
                <div className="px-3 py-2 border-bottom d-flex align-items-center">
                  <label className="d-flex align-items-center gap-2 small mb-0 cursor-pointer">
                    <input type="checkbox" checked={showOnlyCurrentPackage} onChange={(e) => setShowOnlyCurrentPackage(e.target.checked)} />
                    <span>แสดงเฉพาะแพ็กเกจปัจจุบัน ({selectedPackage.name || selectedPackage.code})</span>
                  </label>
                </div>
              )}
              <div className="modal-body p-0" style={{ minHeight: 0, overflow: 'hidden' }}>
                <div
                  style={{
                    maxHeight: 'min(420px, calc(100vh - 240px))',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch',
                  }}
                >
                <table className="table table-striped table-bordered text-center mb-0" style={{ minWidth: 640 }}>
                  <thead className="table-dark"><tr><th>ลำดับ</th><th>โพย</th><th>รอบ</th><th>แพ็กเกจ</th><th></th></tr></thead>
                  <tbody>
                    {loadingOldSlips ? (
                      <tr><td colSpan={5} className="text-center text-muted py-3">กำลังโหลด...</td></tr>
                    ) : (() => {
                      const list = showOnlyCurrentPackage && selectedPackage?.code ? oldSlipList.filter((s) => s.packageCode === selectedPackage.code) : oldSlipList
                      if (list.length === 0) {
                        return (
                          <tr>
                            <td colSpan={5} className="text-center text-muted py-3">
                              {showOnlyCurrentPackage && selectedPackage?.code ? 'ไม่มีโพยที่ตรงกับแพ็กเกจปัจจุบัน' : 'ไม่มีโพยเก่า'}
                            </td>
                          </tr>
                        )
                      }
                      return list.map((slip, index) => (
                        <tr key={slip._id || slip.slipId}>
                          <td>{index + 1}.</td>
                          <td>{slip.slipId || slip._id || '-'}</td>
                          <td>{slip.huayName || slip.roundNumber || '-'}{slip.createdAt ? ` (${new Date(slip.createdAt).toLocaleString('th-TH', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })})` : ''}</td>
                          <td className="text-nowrap">{slip.packageName || slip.packageCode || '-'}</td>
                          <td><button type="button" className="btn btn-sm btn-primary" onClick={() => handleApplyOldSlip(slip)}>เลือก</button></td>
                        </tr>
                      ))
                    })()}
                  </tbody>
                </table>
                </div>
              </div>
              <div className="modal-footer justify-content-center border-top">
                <button type="button" className="btn btn-secondary" onClick={() => setShowOldSlipModal(false)}>ปิด</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ModernLobbyHuayBetSideLeft
